import { useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { faceLabel, uploadToDraft } from "@/lib/imports";
import { useDraft } from "@/components/import/DraftContext";
import { ImagePicker } from "@/components/import/ImagePicker";

export function FaceSlot({ item, slot }) {
  const { id, assign, refresh, open } = useDraft();
  const [picking, setPicking] = useState(false);
  const [over, setOver] = useState(false);
  const editable = open && item.status === "pending";
  const tid = `import-slot-${item.item_id}-${slot.face}`;

  const drop = async (e) => {
    e.preventDefault();
    setOver(false);
    if (!editable) return;
    const fileId = e.dataTransfer.getData("text/pf-file");
    if (fileId) return assign(item.item_id, slot.face, fileId);
    const files = Array.from(e.dataTransfer.files || []).slice(0, 1);
    if (!files.length) return;
    try {
      const [r] = await uploadToDraft(id, files);
      if (!r.ok) return toast.error(`${r.filename}: ${r.error}`);
      await assign(item.item_id, slot.face, r.file_id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/imports/${id}/items/${item.item_id}/faces/${slot.face}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="w-24 shrink-0" data-testid={tid}>
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
        className={`relative grid aspect-[63/88] place-items-center overflow-hidden rounded-lg border bg-black/40 transition-[border-color] ${over ? "border-aether-400" : slot.file_id ? "border-[#2A3447]" : "border-dashed border-amber-600/60"}`}>
        {slot.file_id ? <img src={assetUrl(slot.url)} alt={faceLabel(item, slot.face)} className="h-full w-full object-contain" data-testid={`${tid}-image`} />
          : <button disabled={!editable} onClick={() => setPicking(true)} className="flex flex-col items-center gap-1 p-2 text-center text-[10px] text-amber-300" data-testid={`${tid}-choose`}><ImagePlus className="h-5 w-5" />Drop or choose</button>}
        {slot.file_id && editable && !slot.from_existing && (
          <button onClick={remove} className="absolute right-1 top-1 rounded bg-black/70 p-0.5 text-slate-300 hover:text-red-300" aria-label="Remove image" data-testid={`${tid}-remove`}><X className="h-3 w-3" /></button>
        )}
      </div>
      <p className="mt-1 truncate text-center font-mono text-[10px] text-slate-500">{faceLabel(item, slot.face)}{slot.from_existing ? " · current" : ""}</p>
      {slot.file_id && editable && <button onClick={() => setPicking(true)} className="w-full text-center text-[10px] text-amber-300 hover:underline" data-testid={`${tid}-replace`}>Replace</button>}
      <ImagePicker open={picking} onClose={() => setPicking(false)} title={`Image for ${faceLabel(item, slot.face).toLowerCase()}`}
        onPick={(fid) => { setPicking(false); assign(item.item_id, slot.face, fid); }} />
    </div>
  );
}
